// Canonical home for word-bar slot classification.
//
// One pure decision tree, three call sites:
//   - render.ts paints the live word bar
//     (correct-canonical → blue, correct-alt → orange, incorrect → grey).
//   - controller.ts enriches each flick event with `slotState` so the share
//     grid can replay the same color the player saw at the moment of the flick.
//   - main.ts diffs the per-slot states across flicks to fire blue/orange/grey
//     hint toasts when a slot transitions.
//
// Two color systems share this classifier as their input:
//
//   Gameplay palette (three-way, live bar):
//     correct-canonical → blue    (letter correct + move follows a winning edge)
//     correct-alt       → orange  (letter correct + move strands the DAG walk)
//     incorrect         → grey    (letter wrong for this slot)
//     empty             → neutral (unfilled)
//
//   Share palette (two-way + terminators, see sharegrid.ts):
//     🟦 ← correct-canonical only
//     🟧 ← correct-alt OR incorrect (deliberate collapse — share grid's mental
//                                    model is "is this slot locked in or not")
//     ⬛ ← empty
//     ↩️ ← undo row terminator
//     ✅ / ⬛ ← win / loss final terminator
//
// Web vs iOS: the iOS SpellingBarView keeps wrong-letter slots unfilled —
// the game there gives only blue / orange signals. The web port adds an
// explicit grey fill for wrong letters so newcomers get a third visual
// signal alongside the two existing colors. The game still does not grade
// wrongness — every wrong letter renders the same grey. CSS targets:
// `--bar-incorrect` token in styles/index.css, `.slot[data-state="incorrect"]`
// rule in styles/board.css.

import type { Puzzle } from './puzzle'
import type { GameState } from './state'
import { walkSolutionGraph } from './solutionGraph'

export type SlotState =
  | 'empty'
  | 'incorrect'
  | 'correct-canonical'
  | 'correct-alt'

// FilledSlotState excludes 'empty' — used in contexts where the slot is known
// to hold a letter (e.g. flick events, the post-render share-grid final row).
export type FilledSlotState = Exclude<SlotState, 'empty'>

// Single-slot DEGRADE classifier — a pure letter-correctness check, used only
// when the path-dependent DAG walk is NOT authoritative (no solution_graph, or
// provenance not length-parallel to the bar; see useSolutionGraphWalk + the
// computeSlotStates fallback below). It can ONLY return:
//   - `letter === undefined`            → 'empty'
//   - right letter for this slot        → 'correct-canonical'
//   - wrong letter for this slot        → 'incorrect'
// It NEVER returns 'correct-alt': canonicality can't be judged without the DAG,
// so a known-correct letter degrades to blue rather than be falsely marked
// orange. This is the only place 'correct-alt' is structurally impossible.
export function classifySlot(
  letter: string | undefined,
  wantLetter: string | undefined,
): SlotState {
  if (letter === undefined) {
    return 'empty'
  }
  if (wantLetter === undefined || letter.toLowerCase() !== wantLetter) {
    return 'incorrect'
  }
  return 'correct-canonical'
}

// The ONE gate that decides whether the path-dependent DAG walk is authoritative
// for this (state, puzzle). True iff:
//   (a) the puzzle carries a solution_graph (future pack data; absent today), AND
//   (b) the recorded provenance is length-parallel to the exited letters.
//
// (b) matters because the walk consumes (sourceCell, direction) per filled slot;
// a legacy/dropped-provenance restore (persistence < v5) would leave provenance
// shorter than the bar, and the walk would mis-classify the unbacked slots. Web
// persists provenance at v5+, so a restored game usually keeps the walk; only
// legacy records degrade.
//
// EXPORTED so the lose condition (controller.ts:checkWinLose) and the live bar
// (render.ts) read the SAME decision and migrate atomically (§4.1, §11): the
// board must never paint from the DAG while the loss check reads the degrade.
export function useSolutionGraphWalk(
  state: GameState,
  puzzle: Puzzle,
): boolean {
  return (
    puzzle.solutionGraph !== undefined &&
    state.exitedProvenance.length === state.exitedLetters.length
  )
}

// THE single source of truth for per-slot classification (length == wordLength,
// 'empty' for unfilled slots). All four consumers — the live bar (render.ts),
// the lose condition (controller.ts), the flick-event enrichment (controller.ts),
// and the hint/share paths (main.ts) — route through this so the bar, the loss
// check, and the share grid agree by construction.
//
// Gated source (§4.1, §11): when useSolutionGraphWalk is true, the stateful DAG
// walk is authoritative; otherwise the pure letter-correctness degrade runs.
// Walk states for slot i depend only on moves 0..i, so a state baked at flick
// time stays valid (no retroactive repaint).
//
// The degrade stays REACHABLE for the provenance-incomplete case: a restored
// daily whose provenance is shorter than its bar (legacy persistence) fails
// useSolutionGraphWalk's length-parity check and falls through here, where each
// filled slot is blue-or-grey on letter correctness alone.
export function computeSlotStates(
  state: GameState,
  puzzle: Puzzle,
): readonly SlotState[] {
  const graph = puzzle.solutionGraph
  if (graph !== undefined && useSolutionGraphWalk(state, puzzle)) {
    // DAG walk is authoritative. The walk classifies only filled slots; pad the
    // remaining (unfilled) slots with 'empty' to keep length == wordLength.
    const walk = walkSolutionGraph(
      graph,
      puzzle.targetWord,
      state.exitedLetters,
      state.exitedProvenance,
    )
    const result: SlotState[] = []
    for (let i = 0; i < state.wordLength; i += 1) {
      const walked = walk.slotStates[i]
      result.push(walked === undefined ? 'empty' : walked)
    }
    return result
  }

  // Degrade: pure letter-correctness, never orange (see classifySlot).
  const result: SlotState[] = []
  const targetLc = puzzle.targetWord.toLowerCase()
  for (let i = 0; i < state.wordLength; i += 1) {
    result.push(classifySlot(state.exitedLetters[i], targetLc[i]))
  }
  return result
}
